import React, { useState } from "react";
import {
  Box,
  Text,
  Stack,
  Heading,
  Button,
  Dialog,
  useDisclosure,
  SimpleGrid,
  Portal,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";

const CoinToss: React.FC = () => {
  const [flipping, setFlipping] = useState(false);
  const [side, setSide] = useState<string | null>(null);
  const [flipKey, setFlipKey] = useState(0); // for re-triggering animation
  const [selectedCoin, setSelectedCoin] = useState<string>("classic");
  const { open, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  // List of coin designs
  const coinDesigns = [
    {
      key: "music",
      label: "Music",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🎵" color="#e1bee7" rim="#6a1b9a" />
      ),
    },
    {
      key: "robot",
      label: "Robot",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🤖" color="#cfd8dc" rim="#455a64" />
      ),
    },
    {
      key: "classic",
      label: "Classic Gold",
      render: (face: string) => <CoinFace face={face} />,
    },
    {
      key: "silver",
      label: "Silver",
      render: (face: string) => <CoinFaceSilver face={face} />,
    },
    {
      key: "cricket",
      label: "Cricket Logo",
      render: (face: string) => <CoinFaceCricket face={face} />,
    },
    {
      key: "bronze",
      label: "Bronze",
      render: (face: string) => (
        <CoinFaceColor face={face} color="#cd7f32" rim="#8c5c2f" />
      ),
    },
    {
      key: "emerald",
      label: "Emerald",
      render: (face: string) => (
        <CoinFaceColor face={face} color="#50c878" rim="#228b22" />
      ),
    },
    {
      key: "ruby",
      label: "Ruby",
      render: (face: string) => (
        <CoinFaceColor face={face} color="#e0115f" rim="#a40046" />
      ),
    },
    {
      key: "sapphire",
      label: "Sapphire",
      render: (face: string) => (
        <CoinFaceColor face={face} color="#0f52ba" rim="#082567" />
      ),
    },
    {
      key: "diamond",
      label: "Diamond",
      render: (face: string) => (
        <CoinFaceColor face={face} color="#b9f2ff" rim="#7fd8e6" />
      ),
    },
    {
      key: "rainbow",
      label: "Rainbow",
      render: (face: string) => <CoinFaceRainbow face={face} />,
    },
    {
      key: "star",
      label: "Star",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="⭐" color="#ffe066" rim="#a67c00" />
      ),
    },
    {
      key: "moon",
      label: "Moon",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🌙" color="#bfc9ca" rim="#566573" />
      ),
    },
    {
      key: "football",
      label: "Football",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="⚽" color="#fff" rim="#222" />
      ),
    },
    {
      key: "basketball",
      label: "Basketball",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🏀" color="#f4a460" rim="#d2691e" />
      ),
    },
    {
      key: "tennis",
      label: "Tennis",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🎾" color="#bfff00" rim="#7cfc00" />
      ),
    },
    {
      key: "hockey",
      label: "Hockey",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🏒" color="#e0e0e0" rim="#222" />
      ),
    },
    {
      key: "fire",
      label: "Fire",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🔥" color="#ff9800" rim="#d84315" />
      ),
    },
    {
      key: "ice",
      label: "Ice",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="❄️" color="#e0f7fa" rim="#00bcd4" />
      ),
    },
    {
      key: "leaf",
      label: "Leaf",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="🍃" color="#a5d6a7" rim="#388e3c" />
      ),
    },
    {
      key: "crown",
      label: "Crown",
      render: (face: string) => (
        <CoinFaceIcon face={face} icon="👑" color="#ffe066" rim="#a67c00" />
      ),
    },
  ];
  // Generic colored coin face
  const CoinFaceColor = ({
    face,
    color,
    rim,
  }: {
    face: string;
    color: string;
    rim: string;
  }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="58" fill={rim} stroke={rim} strokeWidth="2" />
      <circle cx="60" cy="60" r="50" fill={color} />
      <text
        x="60"
        y="70"
        textAnchor="middle"
        fontSize="56"
        fill="#fff"
        fontWeight="bold"
      >
        {face === "Heads" ? "H" : "T"}
      </text>
    </svg>
  );

  // Rainbow coin face
  const CoinFaceRainbow = ({ face }: { face: string }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <defs>
        <radialGradient id="rainbowRim" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="100%" stopColor="#ff69b4" />
        </radialGradient>
        <linearGradient
          id="rainbowFace"
          x1="0"
          y1="0"
          x2="120"
          y2="120"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ff0000" />
          <stop offset="0.2" stopColor="#ff9900" />
          <stop offset="0.4" stopColor="#ffff00" />
          <stop offset="0.6" stopColor="#00ff00" />
          <stop offset="0.8" stopColor="#0000ff" />
          <stop offset="1" stopColor="#8b00ff" />
        </linearGradient>
      </defs>
      <circle
        cx="60"
        cy="60"
        r="58"
        fill="url(#rainbowRim)"
        stroke="#ff69b4"
        strokeWidth="2"
      />
      <circle cx="60" cy="60" r="50" fill="url(#rainbowFace)" />
      <text
        x="60"
        y="70"
        textAnchor="middle"
        fontSize="56"
        fill="#fff"
        fontWeight="bold"
      >
        {face === "Heads" ? "🌈" : "T"}
      </text>
    </svg>
  );

  // Icon coin face
  const CoinFaceIcon = ({
    face,
    icon,
    color,
    rim,
  }: {
    face: string;
    icon: string;
    color: string;
    rim: string;
  }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <circle cx="60" cy="60" r="58" fill={rim} stroke={rim} strokeWidth="2" />
      <circle cx="60" cy="60" r="50" fill={color} />
      <text
        x="60"
        y="70"
        textAnchor="middle"
        fontSize="56"
        fill="#fff"
        fontWeight="bold"
      >
        {face === "Heads" ? icon : "T"}
      </text>
    </svg>
  );
  const flipCoin = () => {
    setFlipping(true);
    setSide(null);
    setFlipKey((k) => k + 1);
    setTimeout(() => {
      const coin = Math.random() < 0.5 ? "Heads" : "Tails";
      setSide(coin);
      setFlipping(false);
    }, 1200);
  };

  // Coin spins in place (classic flip)
  const flipAnim = keyframes`
    0% {
      transform: rotateY(0deg) scale(1) drop-shadow(0 0 0 #fff);
      box-shadow: 0 0 0 #fff;
    }
    10% {
      transform: rotateY(180deg) scale(1.08) drop-shadow(0 0 8px #ffe066);
      box-shadow: 0 0 8px #ffe066;
    }
    25% {
      transform: rotateY(540deg) scale(1.15) drop-shadow(0 0 16px #00bcd4);
      box-shadow: 0 0 16px #00bcd4;
    }
    50% {
      transform: rotateY(900deg) scale(1.2) drop-shadow(0 0 24px #ff9800);
      box-shadow: 0 0 24px #ff9800;
    }
    75% {
      transform: rotateY(1260deg) scale(1.15) drop-shadow(0 0 16px #a5d6a7);
      box-shadow: 0 0 16px #a5d6a7;
    }
    90% {
      transform: rotateY(1620deg) scale(1.08) drop-shadow(0 0 8px #b9f2ff);
      box-shadow: 0 0 8px #b9f2ff;
    }
    100% {
      transform: rotateY(1800deg) scale(1) drop-shadow(0 0 0 #fff);
      box-shadow: 0 0 0 #fff;
    }
  `;

  // Classic Gold Coin
  const CoinFace = ({ face }: { face: string }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <defs>
        <radialGradient id="goldRim" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fffbe6" />
          <stop offset="60%" stopColor="#ffe066" />
          <stop offset="85%" stopColor="#e6b800" />
          <stop offset="100%" stopColor="#a67c00" />
        </radialGradient>
        <radialGradient id="coinFace" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fffde4" />
          <stop offset="70%" stopColor="#ffe066" />
          <stop offset="100%" stopColor="#e6b800" />
        </radialGradient>
        <radialGradient id="shadow" cx="50%" cy="60%" r="60%">
          <stop offset="60%" stopColor="#000" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <linearGradient
          id="shine2"
          x1="0"
          y1="0"
          x2="120"
          y2="120"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" stopOpacity="0.7" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Gold rim */}
      <circle
        cx="60"
        cy="60"
        r="58"
        fill="url(#goldRim)"
        stroke="#a67c00"
        strokeWidth="2"
      />
      {/* Coin face */}
      <circle cx="60" cy="60" r="50" fill="url(#coinFace)" />
      {/* Subtle shadow for 3D effect */}
      <ellipse cx="60" cy="75" rx="38" ry="12" fill="url(#shadow)" />
      {/* Shine */}
      <ellipse
        cx="45"
        cy="45"
        rx="18"
        ry="8"
        fill="url(#shine2)"
        opacity="0.5"
      />
      {/* Cricket faces */}
      {face === "Heads" ? (
        // Heads: Cricket ball with detailed seam
        <g>
          <circle
            cx="60"
            cy="60"
            r="26"
            fill="#c62828"
            stroke="#fff"
            strokeWidth="2.5"
          />
          <ellipse
            cx="60"
            cy="60"
            rx="22"
            ry="5"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
          />
          <ellipse
            cx="60"
            cy="60"
            rx="22"
            ry="5"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            transform="rotate(18 60 60)"
          />
          <ellipse
            cx="60"
            cy="60"
            rx="22"
            ry="5"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            transform="rotate(-18 60 60)"
          />
          {/* Ball shine */}
          <ellipse cx="70" cy="52" rx="7" ry="2.5" fill="#fff" opacity="0.25" />
        </g>
      ) : face === "Tails" ? (
        // Tails: Cricket stumps and bails, more detail
        <g>
          {/* Stumps */}
          <rect
            x="50"
            y="44"
            width="4"
            height="28"
            rx="2"
            fill="#e6b800"
            stroke="#fff"
            strokeWidth="1.2"
          />
          <rect
            x="58"
            y="44"
            width="4"
            height="28"
            rx="2"
            fill="#e6b800"
            stroke="#fff"
            strokeWidth="1.2"
          />
          <rect
            x="66"
            y="44"
            width="4"
            height="28"
            rx="2"
            fill="#e6b800"
            stroke="#fff"
            strokeWidth="1.2"
          />
          {/* Bails */}
          <rect
            x="49"
            y="41"
            width="26"
            height="4"
            rx="2"
            fill="#fffbe6"
            stroke="#e6b800"
            strokeWidth="1.2"
          />
          {/* Ground shadow */}
          <ellipse
            cx="60"
            cy="74"
            rx="14"
            ry="3.5"
            fill="#000"
            opacity="0.08"
          />
        </g>
      ) : null}
    </svg>
  );

  // Silver Coin
  const CoinFaceSilver = ({ face }: { face: string }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <defs>
        <radialGradient id="silverRim" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f8f8f8" />
          <stop offset="60%" stopColor="#bfc9ca" />
          <stop offset="85%" stopColor="#7f8c8d" />
          <stop offset="100%" stopColor="#566573" />
        </radialGradient>
        <radialGradient id="silverFace" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#f8f8f8" />
          <stop offset="70%" stopColor="#bfc9ca" />
          <stop offset="100%" stopColor="#7f8c8d" />
        </radialGradient>
      </defs>
      <circle
        cx="60"
        cy="60"
        r="58"
        fill="url(#silverRim)"
        stroke="#566573"
        strokeWidth="2"
      />
      <circle cx="60" cy="60" r="50" fill="url(#silverFace)" />
      {/* Face icons */}
      {face === "Heads" ? (
        <text x="60" y="70" textAnchor="middle" fontSize="56" fill="#566573">
          H
        </text>
      ) : (
        <text x="60" y="70" textAnchor="middle" fontSize="56" fill="#566573">
          T
        </text>
      )}
    </svg>
  );

  // Cricket Logo Coin
  const CoinFaceCricket = ({ face }: { face: string }) => (
    <svg width="100%" height="100%" viewBox="0 0 120 120">
      <circle
        cx="60"
        cy="60"
        r="58"
        fill="#009688"
        stroke="#004d40"
        strokeWidth="2"
      />
      <circle cx="60" cy="60" r="50" fill="#26a69a" />
      {/* Cricket logo */}
      <text x="60" y="70" textAnchor="middle" fontSize="48" fill="#fff">
        🏏
      </text>
      <text x="60" y="100" textAnchor="middle" fontSize="16" fill="#fff">
        {face}
      </text>
    </svg>
  );

  return (
    <Stack
      direction="column"
      gap={{ base: 6, md: 10 }}
      mt={0}
      align="center"
      bgGradient="linear(to-b, #e0eafc, #cfdef3)"
      minH="100vh"
      h="100vh"
      p={{ base: 4, md: 8 }}
      boxShadow={{ base: "none", md: "2xl" }}
      overflow="hidden"
    >
      <Heading
        size="2xl"
        color="teal.700"
        letterSpacing={2}
        mb={2}
        fontWeight={800}
        textAlign="center"
      >
        Cricket Toss
      </Heading>
      <Text
        fontSize={{ base: "md", md: "xl" }}
        color="gray.700"
        maxW={480}
        textAlign="center"
        mb={2}
      >
        Flip the virtual coin to decide who bats or bowls first! Tap the coin to
        toss, and use the button below to choose your favorite coin design.
      </Text>
      <Box
        key={flipKey}
        w={{ base: 32, sm: 40, md: 56 }}
        h={{ base: 32, sm: 40, md: 56 }}
        borderRadius="full"
        boxShadow="0 8px 32px 0 rgba(0,0,0,0.18)"
        bgGradient="radial(circle at 60% 40%, #ffe066 60%, #e6b800 100%)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        css={
          flipping
            ? {
                perspective: "1000px",
                animation: `${flipAnim} 1.4s cubic-bezier(.4,2,.6,1)`,
                boxShadow: "0 0 32px 8px #ffe066, 0 0 64px 16px #00b4d8",
                zIndex: 2,
              }
            : {
                perspective: "1000px",
                transition: "box-shadow 0.3s, transform 0.3s",
                boxShadow: "0 8px 32px 0 rgba(0,0,0,0.18)",
                zIndex: 1,
              }
        }
        style={{
          cursor: flipping ? "not-allowed" : "pointer",
          opacity: 1,
          transition: "box-shadow 0.3s, transform 0.3s",
        }}
        onClick={() => {
          if (!flipping) flipCoin();
        }}
        tabIndex={0}
        aria-label="Flip coin"
        role="button"
      >
        <Box
          w="96%"
          h="96%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {coinDesigns
            .find((c) => c.key === selectedCoin)
            ?.render(side ? side : "Heads")}
        </Box>
      </Box>
      <Box
        minH="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mt={2}
      >
        {side ? (
          <Text
            fontSize={{ base: "2xl", md: "3xl" }}
            color="teal.700"
            fontWeight="bold"
            letterSpacing={1}
            textAlign="center"
          >
            {side}
          </Text>
        ) : null}
      </Box>
      <Button
        bgGradient="linear(to-r, #4fc3f7, #01579b)"
        color="#fff"
        _hover={{
          bgGradient: "linear(to-r, #01579b, #4fc3f7)",
          boxShadow: "0 0 0 2px #01579b",
        }}
        _active={{ bg: "#01579b" }}
        fontWeight="bold"
        fontSize="lg"
        borderRadius="full"
        px={8}
        py={6}
        shadow="md"
        onClick={onOpen}
        mb={2}
      >
        <Box as="span" mr={2} fontSize="xl">
          🪙
        </Box>{" "}
        Choose Coin Style
      </Button>
      {/* Top right icon button for design switch */}
      <Box position="absolute" top={4} right={4} zIndex={10}>
        <Button
          variant="ghost"
          colorScheme="teal"
          borderRadius="full"
          p={2}
          minW={0}
          onClick={() => navigate("/coin-toss-new")}
          aria-label="Switch to new design"
        >
          <Box as="span" fontSize="2xl">
            ✨
          </Box>
        </Button>
      </Box>
      <Dialog.Root size={{ mdDown: "full", md: "lg" }} open={open}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              borderRadius="2xl"
              p={6}
              maxW="96vw"
              width={{ base: "100vw", md: "480px" }}
            >
              <Dialog.Header>
                <Dialog.Title
                  fontSize={{ base: "lg", md: "xl" }}
                  color="teal.700"
                >
                  <Box as="span" fontSize="2xl" mr={2}>
                    🪙
                  </Box>{" "}
                  Select a Coin
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <SimpleGrid columns={{ base: 2, md: 3 }} gap={4}>
                  {coinDesigns.map((coin) => (
                    <Box
                      key={coin.key}
                      border={
                        selectedCoin === coin.key
                          ? "2px solid #319795"
                          : "1px solid #eee"
                      }
                      borderRadius="md"
                      p={2}
                      cursor="pointer"
                      onClick={() => {
                        setSelectedCoin(coin.key);
                        onClose();
                      }}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      transition="box-shadow 0.2s, border 0.2s"
                      boxShadow={
                        selectedCoin === coin.key ? "0 0 0 2px #4fc3f7" : "none"
                      }
                    >
                      <Box w={16} h={16}>
                        {coin.render(side ?? "Heads")}
                      </Box>
                      <Text
                        mt={2}
                        fontSize="sm"
                        color="teal.700"
                        fontWeight={
                          selectedCoin === coin.key ? "bold" : "normal"
                        }
                        textAlign="center"
                      >
                        {coin.label}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  );
};

export default CoinToss;
